import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '@/services/api';
import { getHub } from '@/services/signalr';
import { usePageTitle } from '@/shared/usePageTitle';
import { PageHeader } from '@/shared/PageHeader';
import { useAuth } from '@/shared/AuthContext';

interface Message { id: string; nickname: string; text: string; sentAt: string; userId: string }

function formatTimestamp(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const diffH = (now.getTime() - date.getTime()) / 3600000;
  if (diffH < 24) return date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  if (diffH < 48) return `Ieri ${date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`;
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')} ${date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`;
}

export function ChatPage(): React.JSX.Element {
  usePageTitle('Chat');
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const { user: me } = useAuth();

  const { data } = useQuery<Message[]>({
    queryKey: ['chat.messages'],
    queryFn: async () => (await api.get('/chat/messages', { params: { limit: 100 } })).data,
  });

  const send = useMutation({
    mutationFn: async () => { await api.post('/chat/messages', { text }); },
    onSuccess: () => { setText(''); qc.invalidateQueries({ queryKey: ['chat.messages'] }); },
  });

  useEffect(() => { api.post('/chat/read').catch(() => { /* ignore */ }); }, []);

  useEffect(() => {
    const conn = getHub('/api/hubs/chat');
    const handler = () => {
      qc.invalidateQueries({ queryKey: ['chat.messages'] });
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('VVF Chat', { body: 'Nuovo messaggio in chat' });
      }
    };
    conn.on('messageReceived', handler);
    // Punto 17: richiesta permission solo una volta (flag localStorage)
    if ('Notification' in window && Notification.permission === 'default'
        && !localStorage.getItem('vvf.notifAsked')) {
      localStorage.setItem('vvf.notifAsked', '1');
      Notification.requestPermission();
    }
    return () => { conn.off('messageReceived', handler); };
  }, [qc]);

  return (
    <div className="page-container">
      <PageHeader title="💬 Chat Distaccamento" />

      <div className="chat-container">
        <div>
          {(data ?? []).map(m => {
            const mine = m.userId === me?.id;
            return (
              <div key={m.id} className={`chat-bubble ${mine ? 'mine' : 'other'}`}>
                <div className="chat-meta">
                  {mine ? 'Tu' : m.nickname} · {formatTimestamp(m.sentAt)}
                </div>
                <div>{m.text}</div>
              </div>
            );
          })}
          {(data ?? []).length === 0 && (
            <div className="empty-state">
              <div style={{ fontSize: 48, marginBottom: 10 }}>💬</div>
              Nessun messaggio. Scrivi il primo!
            </div>
          )}
        </div>
      </div>

      <div className="chat-input-row">
        <input value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && text) { e.preventDefault(); send.mutate(); } }}
          placeholder="Scrivi un messaggio..." />
        <button onClick={() => send.mutate()} disabled={!text} className="btn-success">
          ➤ Invia
        </button>
      </div>
    </div>
  );
}
